import {
  Box,
  Button,
  Divider,
  Icons,
  Typography,
  useMediaQuery,
  useTheme,
  Tooltip,
} from '@ublend-npm/aulaui-core';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createStyles } from './AssignmentHeader.styles';
import { AssignmentHeaderPage } from '../types';
import {
  Assignment,
  ProviderType,
  ResponsiveAssignment,
} from '@core/assignments/types';
import formatDate from '@core/utils/formatDate';
import { VIEW_SUBMISSIONS, YET_TO_RELEASE } from '@app/constants/texts';
import {
  TURNITIN_PROVIDER,
  HANDIN_PROVIDER,
  QUIZZES_PROVIDER,
  LTI1P3_PROVIDER,
  MANUAL,
} from '@core/assignments/constants';
import {
  accessToken as getAccessToken,
  currentUser,
} from '@core/selectors/user';
import showToast from '@core/toasts/operations/showToast.operation';
import TurnitinModal from '../TurnitinModal';
import { Lti1p3Launch } from '../../Assignments/Lti1p3Launch/Lti1p3Launch';
import {
  getAssignmentProvider,
  launchHandin,
  launchLti1p3,
  launchQuizzes,
  launchTurnitin,
  resolveHandinExternalId,
} from '../utils/launchAssignment';
import SubmissionsDownloadButton from './SubmissionDownload/SubmissionsDownloadButton';
import AssignmentTypeProvider, {
  useAssignmentTypeProvider,
} from './SubmissionDownload/AssignmentTypeProvider';
import { AulaState } from '@core/types/state';
import {
  getAssignmentSubmissions,
  getTotalSubmissions,
  hasFetchedSubmissions,
  isFetchingSubmissions,
} from '@core/assignments/selectors';
import { operations as assignmentOperations } from '../../../../../store/assignments';
import useIsClamped from '@app/hooks/useIsClamped';
import launchManualAssignment from '../../Assignments/utils/launchManualAssignment';

interface AssignmentHeaderProps {
  assignment: Assignment;
  page: AssignmentHeaderPage;
  handleViewSubmissions?: () => void;
  isEducator: boolean;
  accessingAsStaff: boolean;
  currentSpaceId: string;
  totalStudentCount?: number;
  totalSubmissionCount?: number;
  isStudent?: boolean;
}
const ASSIGNMENT_HEADER_TITLE_ID = 'assignment-header-title';

interface OpenInProviderButtonProps {
  styles: any;
  provider: ProviderType;
  onClick: () => {};
}

const AssignmentHeader = ({
  assignment,
  page,
  handleViewSubmissions,
  isEducator,
  accessingAsStaff,
  currentSpaceId,
  totalStudentCount,
  totalSubmissionCount,
  isStudent,
}: AssignmentHeaderProps) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const dispatch = useDispatch();
  const user = useSelector(currentUser);
  // States for turnitin and LTI 1.3 launch
  const [turnitinLaunch, setTurnitinLaunch] = useState<Assignment | null>(null);
  const [lti1p3Assignment, setLti1p3Assignment] = useState<Assignment | null>(
    null
  );
  const isBigScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const isTabletScreen = useMediaQuery(
    theme.breakpoints.up('sm') && theme.breakpoints.down('lg')
  );
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = theme?.palette?.mode === 'dark';
  const assignmentId = assignment?.id || assignment?.objectId;

  const accessToken = useSelector((state: AulaState) =>
    getAccessToken(state, currentSpaceId)
  );

  // Check if the assignment is a group assignment
  const isGroupAssignment =
    assignment &&
    (assignment as ResponsiveAssignment)?.groupSet &&
    ((assignment as ResponsiveAssignment)?.groups?.length ?? 0) > 0;

  const isAnonymised = assignment.isAnonymised;
  const hasFetchedAssignmentSubmissions = useSelector((state: AulaState) =>
    assignmentId ? hasFetchedSubmissions(state, assignmentId) : false
  );
  const isFetchingAssignmentSubmissions = useSelector((state: AulaState) =>
    assignmentId ? isFetchingSubmissions(state, assignmentId) : false
  );
  const submissionCount = useSelector((state: AulaState) => {
    if (!assignmentId) {
      return 0;
    }

    const total = getTotalSubmissions(state, assignmentId);
    if (typeof total === 'number') {
      return total;
    }

    return getAssignmentSubmissions(state, assignmentId).length;
  });

  useEffect(() => {
    if (
      !isEducator ||
      !accessToken ||
      !assignmentId ||
      getAssignmentProvider(assignment) !== HANDIN_PROVIDER
    ) {
      return;
    }

    if (hasFetchedAssignmentSubmissions || isFetchingAssignmentSubmissions) {
      return;
    }

    dispatch(
      assignmentOperations.fetchSubmissions({
        accessToken,
        assignmentId,
      })
    );
  }, [
    accessToken,
    assignment,
    assignmentId,
    dispatch,
    hasFetchedAssignmentSubmissions,
    isEducator,
    isFetchingAssignmentSubmissions,
  ]);

  const {
    title,
    description,
    startDate,
    endDate,
    gradeType,
    gradesReleasedAt,
    linkedMaterials,
    provider,
  } = assignment;

  // iife for parsing the description
  const assignmentDescription = (() => {
    if (!description || typeof description !== 'string') {
      return '';
    }

    try {
      const parsedDescription = JSON.parse(description);
      if (parsedDescription && typeof parsedDescription === 'object') {
        return parsedDescription?.value?.[0]?.children?.[0]?.text ?? '';
      }
      return description;
    } catch {
      return '';
    }
  })();

  const PROVIDERS = {
    'hand-in': {
      title: 'Handin',
      accent: isDark ? '#B4E8FB' : '#007D99',
      hoverBg: isDark ? '#B4E8FB1A' : '#007D990A',
    },
    turnitin: {
      title: 'Turnitin',
      accent: '#FF6666',
      hoverBg: isDark ? '#FF66661A' : '#FF66660A',
    },
  };

  const PROVIDER_KEYS = Object.keys(PROVIDERS);

  // Check if the user can view the submissions button
  const canViewSubmissionsButton =
    Boolean(currentSpaceId && assignmentId) &&
    page === 'overview' &&
    PROVIDER_KEYS.includes(provider);

  const GRADE_TYPES = {
    percentage: 'Percentage',
    letterGrade: 'Letter grade',
    points: 'Points',
    passFail: 'Pass/Fail',
    freeForm: 'Free form grade',
  };
  const GRADE_TYPE_LABEL =
    GRADE_TYPES[gradeType as keyof typeof GRADE_TYPES] || gradeType || 'NA';

  const ASSIGNMENT_METADATA = useMemo(() => {
    return [
      {
        id: 'open-date',
        label: 'Open date',
        value: startDate ? formatDate(startDate) : 'NA',
        icon: (
          <Icons.CalendarMonthOutlined sx={{ fontSize: '20px' }} aria-hidden />
        ),
      },
      {
        id: 'due-date',
        label: 'Due date',
        value: endDate ? formatDate(endDate) : 'NA',
        icon: (
          <Icons.CalendarMonthOutlined sx={{ fontSize: '20px' }} aria-hidden />
        ),
      },
      {
        id: 'grades-release-date',
        label: 'Grades release date',
        value: gradesReleasedAt ? formatDate(gradesReleasedAt) : YET_TO_RELEASE,
        icon: <Icons.ChecklistOutlined sx={{ fontSize: '20px' }} aria-hidden />,
      },
      {
        id: 'grade-type',
        label: 'Grade type',
        value: GRADE_TYPE_LABEL,
        icon: <Icons.StarBorder sx={{ fontSize: '20px' }} aria-hidden />,
      },
    ];
  }, [startDate, endDate, GRADE_TYPE_LABEL, gradesReleasedAt]);

  // Text overflow check for Title, extension title and description
  const [titleRef, isTitleClamped] = useIsClamped<HTMLDivElement>();
  const [descriptionRef, isDescriptionClamped] = useIsClamped<HTMLDivElement>();
  const [extensionTitleRef, isExtensionTitleClamped] =
    useIsClamped<HTMLDivElement>();

  // Launch assignments based on provider
  const launchAssignment = async () => {
    if (!assignment) {
      return;
    }
    const spaceId = assignment?.classRoom?.objectId || currentSpaceId;

    switch (getAssignmentProvider(assignment)) {
      case HANDIN_PROVIDER:
        launchHandin({
          assignmentId:
            assignment?.externalId ||
            (await resolveHandinExternalId(assignment, spaceId)),
          spaceId,
        });
        break;
      case TURNITIN_PROVIDER:
        launchTurnitin(assignment, setTurnitinLaunch);
        break;
      case QUIZZES_PROVIDER:
        launchQuizzes({ assignment, spaceId });
        break;
      case LTI1P3_PROVIDER:
        launchLti1p3(assignment, setLti1p3Assignment);
        break;
      case MANUAL:
        launchManualAssignment({ assignmentId, spaceId });
        break;
      default:
        break;
    }
  };

  // Handle view submission button click
  const onViewSubmissions = () => {
    if (!canViewSubmissionsButton || !handleViewSubmissions) {
      return;
    }
    if (isEducator || accessingAsStaff) {
      handleViewSubmissions();
      return;
    }
  };

  // Open in Provider button
  const OpenInProviderButton = ({
    styles,
    provider,
    onClick,
  }: OpenInProviderButtonProps) => {
    // Get the details from the context for open in button
    const {
      type,
      title: PROVIDER_TITLE,
      renderImage,
    } = useAssignmentTypeProvider();

    // If the provider is manual and the user is a student, we don't show the button
    if (type === MANUAL && isStudent) {
      return null;
    }

    return (
      <Button
        type="button"
        kind="secondary"
        sx={{
          ...styles.providerButton,
          color: PROVIDERS[provider]?.accent
            ? PROVIDERS[provider]?.accent
            : 'default',
          borderColor: PROVIDERS[provider]?.accent
            ? PROVIDERS[provider]?.accent
            : 'default',
          '&:hover': {
            backgroundColor: PROVIDERS[provider]?.hoverBg
              ? PROVIDERS[provider]?.hoverBg
              : 'default',
            borderColor: PROVIDERS[provider]?.accent
              ? PROVIDERS[provider]?.accent
              : 'default',
          },
        }}
        onClick={onClick}
      >
        <Box
          component="span"
          aria-hidden="true"
          sx={{ display: 'inline-flex' }}
        >
          {renderImage({})}
        </Box>
        {`Open in ${PROVIDER_TITLE}`}
        <Icons.OpenInNew fontSize="small" aria-hidden />
      </Button>
    );
  };

  // Get the extension name
  const getExtensionName = ({
    title,
    parentAssignmentId,
    extensions,
  }: Assignment) => {
    if (parentAssignmentId) {
      return title;
    }

    if (extensions?.length) {
      return 'Main cohort';
    }
    return '';
  };

  // Get the submission count vs total students count
  const submissionCountVsTotal =
    totalStudentCount === 0
      ? '0 students'
      : `${totalSubmissionCount}/${totalStudentCount} submissions`;

  const extensionTitle =
    page === 'overview'
      ? title
      : `${getExtensionName(assignment)} (${submissionCountVsTotal})`;

  return (
    <>
      <Box
        sx={styles.headerCard}
        role="region"
        aria-labelledby={ASSIGNMENT_HEADER_TITLE_ID}
      >
        {page === 'overview' && (
          <Box>
            <Tooltip
              placement="bottom"
              disableInteractive
              title={title}
              disableFocusListener={!isTitleClamped || isMobile}
              disableHoverListener={!isTitleClamped || isMobile}
              disableTouchListener={!isTitleClamped || isMobile}
            >
              <Box
                ref={titleRef}
                id={ASSIGNMENT_HEADER_TITLE_ID}
                sx={styles.title}
              >
                {title}
              </Box>
            </Tooltip>
            {assignmentDescription && (
              <Tooltip
                placement="bottom"
                disableInteractive
                title={assignmentDescription}
                disableFocusListener={!isDescriptionClamped || isMobile}
                disableHoverListener={!isDescriptionClamped || isMobile}
                disableTouchListener={!isDescriptionClamped || isMobile}
              >
                <Box ref={descriptionRef} sx={styles.description}>
                  {assignmentDescription}
                </Box>
              </Tooltip>
            )}
          </Box>
        )}
        {page === 'assignments' && (
          <Box>
            <Tooltip
              placement="bottom"
              disableInteractive
              title={extensionTitle}
              disableFocusListener={!isExtensionTitleClamped || isMobile}
              disableHoverListener={!isExtensionTitleClamped || isMobile}
              disableTouchListener={!isExtensionTitleClamped || isMobile}
            >
              <Box
                ref={extensionTitleRef}
                id={ASSIGNMENT_HEADER_TITLE_ID}
                sx={styles.title}
              >
                {extensionTitle}
              </Box>
            </Tooltip>
          </Box>
        )}

        {linkedMaterials &&
          linkedMaterials.length > 0 &&
          linkedMaterials.map((extension: any) => (
            <Box
              key={extension?.id || extension?.title}
              sx={styles.materialTab}
              aria-label={`Linked material: ${extension?.title}`}
            >
              <Icons.BookOutlined sx={styles.materialTabIcon} aria-hidden />
              <Box sx={styles.materialTabText}>{extension?.title}</Box>
              <Icons.ArrowForwardIos sx={styles.materialTabArrow} aria-hidden />
            </Box>
          ))}

        <Box
          sx={styles.metadataRow}
          role="list"
          aria-label="Assignment details"
        >
          {ASSIGNMENT_METADATA.map((item) => (
            <Box key={item.id} sx={styles.metadataItem} role="listitem">
              <Box sx={styles.metadataIcon} aria-hidden="true">
                {item.icon}
              </Box>
              <Box>
                <Typography sx={styles.metadataLabel}>{item.label}</Typography>
                <Typography sx={styles.metadataValue}>{item.value}</Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Divider sx={styles.divider} aria-hidden="true" />
        <Box
          className={isEducator ? 'educatorView' : 'studentView'}
          role="group"
          aria-label="Assignment actions"
          sx={{
            ...(isBigScreen ? styles.actionsRow : styles.actionsRowMobile),
            ...(isTabletScreen && !isEducator
              ? { gridTemplateColumns: '1fr' }
              : {}),
          }}
        >
          <AssignmentTypeProvider
            assignment={assignment}
            spaceId={currentSpaceId}
            user={user ?? undefined}
          >
            <OpenInProviderButton
              styles={styles}
              provider={provider}
              onClick={() => launchAssignment()}
            />
            {(isEducator || accessingAsStaff) && (
              <>
                {getAssignmentProvider(assignment) === HANDIN_PROVIDER && (
                  <SubmissionsDownloadButton
                    styles={styles}
                    currentUserIsEducator={isEducator}
                    assignmentExternalId={assignment.externalId}
                    accessToken={accessToken || ''}
                    isGroupAssignment={isGroupAssignment ?? false}
                    isAnonymisedAssignment={isAnonymised}
                    submissionCount={submissionCount}
                  />
                )}
                {canViewSubmissionsButton && (
                  <Button
                    className={isTabletScreen ? 'full-width' : ''}
                    type="button"
                    kind="secondary"
                    sx={styles.purpleButton}
                    onClick={onViewSubmissions}
                    disabled={isGroupAssignment} // Disable the button if the assignment is a group assignment
                  >
                    {VIEW_SUBMISSIONS}
                  </Button>
                )}
              </>
            )}
          </AssignmentTypeProvider>
        </Box>
      </Box>
      {turnitinLaunch && (
        <TurnitinModal
          assignment={{
            ...turnitinLaunch,
            objectId: turnitinLaunch?.objectId || turnitinLaunch?.id,
            title: turnitinLaunch?.title,
            description: turnitinLaunch?.description || '',
            endDate: turnitinLaunch?.endDate || '',
            startDate: turnitinLaunch?.startDate || '',
            gradesReleasedAt: turnitinLaunch?.gradesReleasedAt || '',
            maxGrade: turnitinLaunch?.maxGrade,
          }}
          user={user ?? undefined}
          space={{ objectId: currentSpaceId }}
          isEducator={isEducator}
          onClose={() => setTurnitinLaunch(null)}
        />
      )}
      {lti1p3Assignment?.providerId && lti1p3Assignment?.resourceLinkId && (
        <Lti1p3Launch
          providerId={lti1p3Assignment?.providerId}
          resourceLinkId={lti1p3Assignment?.resourceLinkId}
          resourceLinkTitle={lti1p3Assignment?.title}
          assignmentId={lti1p3Assignment?.id || lti1p3Assignment?.objectId}
          onSuccessLaunch={() => setLti1p3Assignment(null)}
          onErrorLaunch={() => {
            setLti1p3Assignment(null);
            dispatch(
              showToast({
                message: 'There was an error when trying to open the LTI tool.',
              })
            );
          }}
        />
      )}
    </>
  );
};

export default AssignmentHeader;
